/**
 * Sélection de billets + tarifs libres + capacités globales pour /b/[slug].
 *
 * Centralise :
 *  - selections / flexiblePrices state
 *  - increment / decrement / max par tarif
 *  - capacité globale, places restantes, hasAvailableTickets
 *  - calcul totalPrice / ticketsOnlyPrice / optionsTotal
 *  - flexible suggestions
 */
import type { Event, TicketType, TicketOption } from "~/types/event";
import { LIMITE_GRATUITS_PAR_PERSONNE } from "@donoor/utils";

/** Tarif gratuit d'office (pas un tarif libre, qui peut se payer). */
function estTarifGratuit(tt: TicketType): boolean {
  if (tt.pricing === "free") return true;
  return (tt.pricing ?? "fixed") === "fixed" && Number(tt.price || 0) <= 0;
}

interface AttendeeLike {
  selectedOptions: Record<string, number>;
}

export function useEventTicketSelection(opts: {
  event: Ref<Event | null>;
  ticketTypes: Ref<TicketType[]>;
  ticketOptions: Ref<TicketOption[]>;
  attendees: Ref<AttendeeLike[]>;
  promoDiscount: Ref<number>;
}) {
  const selections = ref<Record<string, number>>({});
  const flexiblePrices = ref<Record<string, number>>({});

  function flexibleSuggestions(tt: TicketType): number[] {
    const min = Number(tt.min_price || 0);
    const max = tt.max_price != null ? Number(tt.max_price) : null;
    if (min <= 0) return [];
    const candidates = [min, min * 2, min * 5].map((v) => Math.round(v));
    const unique = [...new Set(candidates)];
    return max !== null ? unique.filter((v) => v <= max) : unique;
  }

  function getOptionsForTicketType(ticketTypeId: string) {
    return opts.ticketOptions.value.filter((opt) => {
      if (!opt.is_active) return false;
      if (opt.applies_to === "all") return true;
      if (opt.applies_to === "custom" && Array.isArray(opt.applies_to_ids)) {
        return opt.applies_to_ids.includes(ticketTypeId);
      }
      return false;
    });
  }

  const totalSoldAllTypes = computed(() =>
    opts.ticketTypes.value.reduce((sum, tt) => sum + (tt.sold_count || 0), 0),
  );

  const totalCapacityAllTypes = computed(() => {
    const activeTypes = opts.ticketTypes.value.filter(tt => tt.is_active);
    if (activeTypes.some(tt => tt.max_quantity === null || tt.max_quantity === undefined)) return null;
    return activeTypes.reduce((sum, tt) => sum + (tt.max_quantity || 0), 0);
  });

  const totalRemainingPlaces = computed(() => {
    if (opts.event.value?.max_capacity) {
      return Math.max(0, opts.event.value.max_capacity - totalSoldAllTypes.value);
    }
    if (totalCapacityAllTypes.value === null) return null;
    return Math.max(0, totalCapacityAllTypes.value - totalSoldAllTypes.value);
  });

  const effectiveCapacity = computed(() => {
    if (opts.event.value?.max_capacity) return opts.event.value.max_capacity;
    return totalCapacityAllTypes.value;
  });

  const isGlobalCapacityReached = computed(() => {
    if (!opts.event.value?.max_capacity) return false;
    return totalSoldAllTypes.value >= opts.event.value.max_capacity;
  });

  const hasAvailableTickets = computed(() => {
    if (isGlobalCapacityReached.value) return false;
    return opts.ticketTypes.value.some(tt => {
      if (!tt.is_active) return false;
      if (tt.max_quantity === null || tt.max_quantity === undefined) return true;
      return (tt.sold_count || 0) < tt.max_quantity;
    });
  });

  const totalSelected = computed(() =>
    Object.values(selections.value).reduce((s, v) => s + v, 0),
  );

  function getRemainingForDisplay(tt: TicketType): number | null {
    // Préférer le champ calculé serveur (`remaining`) quand il est fourni
    // (payload public enrichi) — source de vérité partagée avec l'anti-oversell.
    // Fallback : dérivation locale `max_quantity − sold_count` (compat SSR /
    // anciens payloads). Karim 2026-06-23.
    if (tt.remaining !== undefined) return tt.remaining;
    const perTarif = tt.max_quantity ? Math.max(0, tt.max_quantity - (tt.sold_count || 0)) : null;
    const globalRemaining = opts.event.value?.max_capacity
      ? Math.max(0, opts.event.value.max_capacity - totalSoldAllTypes.value)
      : null;
    if (perTarif !== null && globalRemaining !== null) return Math.min(perTarif, globalRemaining);
    if (perTarif !== null) return perTarif;
    if (globalRemaining !== null) return globalRemaining;
    return null;
  }

  /**
   * Un tarif est COMPLET si le serveur le dit (`sold_out`) ou si les places
   * restantes effectives tombent à 0. `null` (illimité) n'est jamais complet.
   */
  function isSoldOut(tt: TicketType): boolean {
    if (tt.sold_out === true) return true;
    const remaining = getRemainingForDisplay(tt);
    return remaining !== null && remaining <= 0;
  }

  function getMaxForTicketType(tt: TicketType): number {
    const perTarif = tt.max_quantity ? Math.max(0, tt.max_quantity - (tt.sold_count || 0)) : Infinity;
    let globalMax = Infinity;
    if (opts.event.value?.max_capacity) {
      const globalRemaining = Math.max(0, opts.event.value.max_capacity - totalSoldAllTypes.value - totalSelected.value);
      globalMax = (selections.value[tt.id] || 0) + globalRemaining;
    }
    let perOrder = Infinity;
    if (tt.max_per_order) perOrder = tt.max_per_order;
    // Billet GRATUIT sans limite posée par l'association : 15 par personne
    // (l'API compte aussi les commandes précédentes — Karim 2026-10-09).
    else if (estTarifGratuit(tt)) perOrder = LIMITE_GRATUITS_PAR_PERSONNE;
    return Math.min(perTarif, globalMax, perOrder);
  }

  /**
   * Clamp réactif : dès que la disponibilité change (ex. une place se vend en
   * live via le socket → `sold_count`/`remaining` mis à jour), on réduit toute
   * quantité déjà choisie au plafond courant — et on la met à 0 si le tarif
   * devient complet. Sans ça, une quantité sélectionnée AVANT que le tarif se
   * remplisse traversait jusqu'au paiement (→ 409 backend). Karim 2026-06-23.
   *
   * La SOURCE du watch est dérivée de `getRemainingForDisplay` (qui ne lit PAS
   * `selections`) → écrire dans `selections` ici ne re-déclenche jamais le watch
   * (pas de boucle infinie).
   */
  watch(
    () =>
      opts.ticketTypes.value.map((tt) => ({
        id: tt.id,
        remaining: getRemainingForDisplay(tt),
        maxPerOrder: tt.max_per_order ?? null,
      })),
    (rows) => {
      for (const { id, remaining, maxPerOrder } of rows) {
        const current = selections.value[id] || 0;
        if (current <= 0) continue;
        // Plafond = min(places restantes, max par commande). `null` = illimité.
        let ceil = current;
        if (remaining !== null) ceil = Math.min(ceil, Math.max(0, remaining));
        if (maxPerOrder !== null) ceil = Math.min(ceil, maxPerOrder);
        if (current > ceil) selections.value[id] = ceil;
      }
    },
    { deep: true },
  );

  function increment(id: string, max: number) {
    const current = selections.value[id] || 0;
    if (current < max) selections.value[id] = current + 1;
  }

  function decrement(id: string) {
    const current = selections.value[id] || 0;
    if (current > 0) selections.value[id] = current - 1;
  }

  /**
   * Définit le tarif libre saisi pour un type de billet. La mutation vit
   * ici (état propriétaire du ref) plutôt que dans le composant enfant
   * via v-model sur une prop. Cf. vue/no-mutating-props.
   */
  function setFlexiblePrice(id: string, value: number) {
    flexiblePrices.value[id] = value;
  }

  const ticketsOnlyPrice = computed(() => {
    let total = 0;
    for (const [id, qty] of Object.entries(selections.value)) {
      const tt = opts.ticketTypes.value.find(t => t.id === id);
      if (!tt) continue;
      if (tt.pricing === "flexible" || (Number(tt.price) === 0 && flexiblePrices.value[id])) {
        total += (flexiblePrices.value[id] || 0) * qty;
      } else {
        total += Number(tt.price) * qty;
      }
    }
    return total;
  });

  const optionsTotal = computed(() => {
    let total = 0;
    for (const a of opts.attendees.value) {
      for (const [optId, qty] of Object.entries(a.selectedOptions)) {
        const opt = opts.ticketOptions.value.find((o) => o.id === optId);
        if (opt && qty > 0) total += Number(opt.price || 0) * qty;
      }
    }
    return total;
  });

  const totalPrice = computed(() => {
    let total = ticketsOnlyPrice.value;
    for (const a of opts.attendees.value) {
      if (a.selectedOptions) {
        for (const [optId, qty] of Object.entries(a.selectedOptions)) {
          const opt = opts.ticketOptions.value.find((o) => o.id === optId);
          if (opt && qty > 0) total += Number(opt.price || 0) * qty;
        }
      }
    }
    total = Math.max(0, total - opts.promoDiscount.value);
    return total;
  });

  return {
    selections,
    flexiblePrices,
    flexibleSuggestions,
    getOptionsForTicketType,
    totalSoldAllTypes,
    totalRemainingPlaces,
    effectiveCapacity,
    hasAvailableTickets,
    totalSelected,
    getRemainingForDisplay,
    isSoldOut,
    getMaxForTicketType,
    increment,
    decrement,
    setFlexiblePrice,
    ticketsOnlyPrice,
    optionsTotal,
    totalPrice,
  };
}
